import type {
  ClinicModule,
  CreateClinicInput,
  NotificationChannel,
  SaveClinicKeysInput,
  SaveSubscriptionInput,
  SpecialtyCode,
  VendorClinicsQuery,
  VendorForgotPasswordInput,
  VendorLoginInput,
  VendorResetPasswordInput,
} from "@clinic/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { vendorApi } from "@web/vendor/api";
import { VENDOR_QUERY_KEY } from "@web/vendor/constants";

export const useVendorSession = () =>
  useQuery({ queryKey: VENDOR_QUERY_KEY.SESSION, queryFn: vendorApi.session, retry: false });

export function useSignIn() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: VendorLoginInput) => vendorApi.signIn(input),
    onSuccess: (profile) => client.setQueryData(VENDOR_QUERY_KEY.SESSION, profile),
  });
}

export const useForgotPassword = () =>
  useMutation({
    mutationFn: (input: VendorForgotPasswordInput) => vendorApi.forgotPassword(input),
  });

export const useResetPassword = () =>
  useMutation({ mutationFn: (input: VendorResetPasswordInput) => vendorApi.resetPassword(input) });

export function useSignOut() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: vendorApi.signOut,
    onSettled: () => client.clear(),
  });
}

export const useOverview = () =>
  useQuery({ queryKey: VENDOR_QUERY_KEY.OVERVIEW, queryFn: vendorApi.overview });

export const useClinics = (params: Partial<VendorClinicsQuery>) =>
  useQuery({
    queryKey: [...VENDOR_QUERY_KEY.CLINICS, params],
    queryFn: () => vendorApi.clinics(params),
    placeholderData: (previous) => previous,
  });

export const useClinic = (id: string) =>
  useQuery({ queryKey: [...VENDOR_QUERY_KEY.CLINIC, id], queryFn: () => vendorApi.clinic(id) });

export const usePlans = () =>
  useQuery({ queryKey: VENDOR_QUERY_KEY.PLANS, queryFn: vendorApi.plans });

function useRefreshAll() {
  const client = useQueryClient();

  return () =>
    Promise.all(
      [
        VENDOR_QUERY_KEY.OVERVIEW,
        VENDOR_QUERY_KEY.CLINICS,
        VENDOR_QUERY_KEY.CLINIC,
        VENDOR_QUERY_KEY.PLANS,
      ].map((queryKey) => client.invalidateQueries({ queryKey })),
    );
}

export function useSaveSubscription(clinicId: string) {
  const refresh = useRefreshAll();

  return useMutation({
    mutationFn: (input: SaveSubscriptionInput) => vendorApi.saveSubscription(clinicId, input),
    onSuccess: refresh,
  });
}

export function useCreateClinic() {
  const refresh = useRefreshAll();

  return useMutation({
    mutationFn: (input: CreateClinicInput) => vendorApi.createClinic(input),
    onSuccess: refresh,
  });
}

export function useRemoveClinic() {
  const refresh = useRefreshAll();

  return useMutation({
    mutationFn: (clinicId: string) => vendorApi.removeClinic(clinicId),
    onSuccess: refresh,
  });
}

export function useRestoreClinic() {
  const refresh = useRefreshAll();

  return useMutation({
    mutationFn: (clinicId: string) => vendorApi.restoreClinic(clinicId),
    onSuccess: refresh,
  });
}

export function useSaveSubscriptionFor() {
  const refresh = useRefreshAll();

  return useMutation({
    mutationFn: ({
      clinicId,
      input,
    }: {
      readonly clinicId: string;
      readonly input: SaveSubscriptionInput;
    }) => vendorApi.saveSubscription(clinicId, input),
    onSuccess: refresh,
  });
}

export function useSaveDomain(clinicId: string) {
  const refresh = useRefreshAll();

  return useMutation({
    mutationFn: (domain: string | null) => vendorApi.saveDomain(clinicId, { domain }),
    onSuccess: refresh,
  });
}

export function useSaveAppDomain(clinicId: string) {
  const refresh = useRefreshAll();

  return useMutation({
    mutationFn: (appDomain: string | null) => vendorApi.saveAppDomain(clinicId, { appDomain }),
    onSuccess: refresh,
  });
}

export function useSaveKeys(clinicId: string) {
  const refresh = useRefreshAll();

  return useMutation({
    mutationFn: (input: SaveClinicKeysInput) => vendorApi.saveKeys(clinicId, input),
    onSuccess: refresh,
  });
}

export function useSaveMessageChannel(clinicId: string) {
  const refresh = useRefreshAll();

  return useMutation({
    mutationFn: (channel: NotificationChannel) =>
      vendorApi.saveMessageChannel(clinicId, { channel }),
    onSuccess: refresh,
  });
}

export function useSaveModules(clinicId: string) {
  const refresh = useRefreshAll();

  return useMutation({
    mutationFn: (modules: ClinicModule[]) => vendorApi.saveModules(clinicId, { modules }),
    onSuccess: refresh,
  });
}

export function useRevokeSpecialty(clinicId: string) {
  const refresh = useRefreshAll();

  return useMutation({
    mutationFn: (code: SpecialtyCode) => vendorApi.revokeSpecialty(clinicId, code),
    onSuccess: refresh,
  });
}

export function useGrantSpecialty(clinicId: string) {
  const refresh = useRefreshAll();

  return useMutation({
    mutationFn: (code: SpecialtyCode) => vendorApi.grantSpecialty(clinicId, { code }),
    onSuccess: refresh,
  });
}
